import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CalendarFilterSelector from "components/CalendarFilterSelector";
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Divider,
  IconButton,
  Button,
  Popover,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";
import AdjustRoundedIcon from "@mui/icons-material/AdjustRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import dayjs from "dayjs";
import api from "services/api";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { OVERLAY_NAMES } from "constants/Overlays";
import {
  buildEmailThreadLink,
  getProjectNumber,
  parseEmailThreadIds,
} from "helpers/ServiceHelpers";
import { buildMultiTimezoneLines } from "helpers/timezoneHelpers";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";

// Styled HtmlTooltip for rich content
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.primary.text,
    maxWidth: 340,
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.pxToRem(14),
  },
}));

// Resolve the interpretation type icon for calendar events.
// Virtual/Video types use a videocam icon, In-person uses a person icon,
// and Phone/OPI use a phone icon. Returns null when the type is unknown.
const getInterpretationTypeInfo = (svc) => {
  const typeName = String(
    svc?.ServiceSubtype?.Name || svc?.InterpretationType || "",
  )
    .trim();
  const normalized = typeName.toLowerCase();

  let icon = null;
  if (
    normalized.includes("virtual") ||
    normalized.includes("video") ||
    normalized.includes("on-demand")
  ) {
    icon = <VideocamRoundedIcon fontSize="small" />;
  } else if (
    normalized.includes("in-person") ||
    normalized.includes("in person") ||
    normalized.includes("onsite") ||
    normalized.includes("on-site")
  ) {
    icon = <PersonRoundedIcon fontSize="small" />;
  } else if (
    normalized.includes("phone") ||
    normalized === "opi"
  ) {
    icon = <PhoneRoundedIcon fontSize="small" />;
  }

  return { typeName, icon };
};

// Memoized Time Slot Cell Component
const TimeSlotCell = React.memo(
  ({
    isSelected,
    onMouseDown,
    onMouseEnter,
    slotId,
    theme,
    isSelecting,
    isHalfHour,
    isWeekend,
    columnBackgroundColor,
  }) => {
    return (
      <Box
        key={slotId}
        onMouseDown={onMouseDown}
        onMouseEnter={isSelecting ? onMouseEnter : undefined}
        sx={{
          backgroundColor: isSelected
            ? theme.palette.primary[300]
            : columnBackgroundColor ||
              (isWeekend
                ? theme.palette.background.alt
                : theme.palette.background.alt),
          border: isSelected
            ? `2px solid ${theme.palette.primary[300]}`
            : undefined,
          cursor: "pointer",
          transition: "all 0.15s ease",
          "&:hover": {
            backgroundColor: isSelected
              ? theme.palette.primary[400]
              : theme.palette.action.hover,
          },
          position: "relative",
          minHeight: "40px",
          // Keep the bottom divider visible; dashed for half-hour rows
          borderBottom: `${isHalfHour ? "1px solid" : "1px dashed"} ${
            theme.palette.divider
          }`,
        }}
      />
    );
  },
);

TimeSlotCell.displayName = "TimeSlotCell";

// Scroll position memory for the calendar grid, keyed by calendar context
// (effective view mode + anchor date). Module-level on purpose: opening a
// service profile unmounts the Calendar, so instance refs cannot hold it —
// coming back from a profile restores the position the user left instead of
// re-centering on now.
const CALENDAR_SCROLL_MEMORY = new Map();

const Calendar = () => {
  usePageTitle("Calendar");

  const theme = useTheme();
  const {
    handleErrorDialog,
    setOverlayData,
    setOverlayName,
    showSnackbar,
    refreshPage,
    triggerPageRefresh,
  } = useStateContext();
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  // Outlook sync management is admin-only (the server also enforces this).
  const isAdmin = currentUser?.Role === "Admin";

  // Calendar state - always showing current week
  const [selectedStartSlot, setSelectedStartSlot] = useState(null);
  const [selectedEndSlot, setSelectedEndSlot] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // Filter state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("calendarViewMode");
    return saved || "Work Week";
  });
  const [statusFilters, setStatusFilters] = useState(() => {
    const saved = localStorage.getItem("calendarStatusFilters");
    return saved
      ? JSON.parse(saved)
      : ["Requested", "Confirmed", "Completed", "Cancelled"];
  });

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = useState();
  const [hoveredServiceKey, setHoveredServiceKey] = useState(null);
  const [expandedMobileServiceKey, setExpandedMobileServiceKey] =
    useState(null);

  // Overlay/Assignment creation state
  const [listOfClients, setListOfClients] = useState([]);
  const [listOfClientsOPIMinimums, setListOfClientsOPIMinimums] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [isLoadingOverlayData, setIsLoadingOverlayData] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  // Phones: header actions collapse to 40×40 icon buttons (list-screen pattern)
  const isMobile = !useMediaQuery("(min-width:600px)");
  const effectiveViewMode = isSmallScreen ? "Day" : viewMode;

  const handleViewModeChange = (nextViewMode) => {
    if (!nextViewMode || isSmallScreen) return;
    setViewMode(nextViewMode);
  };

  useEffect(() => {
    if (isSmallScreen) {
      setHoveredServiceKey(null);
    } else {
      setExpandedMobileServiceKey(null);
    }
  }, [isSmallScreen]);

  // Save viewMode to localStorage whenever it changes (desktop only)
  useEffect(() => {
    if (isSmallScreen) return;
    localStorage.setItem("calendarViewMode", viewMode);
  }, [viewMode, isSmallScreen]);

  // Save statusFilters to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      "calendarStatusFilters",
      JSON.stringify(statusFilters),
    );
  }, [statusFilters]);

  // Fetch overlay data (clients, linguists, languages, statuses, types)
  const fetchOverlayData = async () => {
    setIsLoadingOverlayData(true);
    try {
      const [
        clientsResponse,
        clientsOPIMinimumsResponse,
        linguistsResponse,
        languagesResponse,
        statusesResponse,
        typesResponse,
      ] = await Promise.all([
        api.get("/clients/brief"),
        api.get("/clients/OPIMinimums"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
      ]);

      if (clientsResponse?.data) setListOfClients(clientsResponse.data);
      if (clientsOPIMinimumsResponse?.data)
        setListOfClientsOPIMinimums(clientsOPIMinimumsResponse.data);
      if (linguistsResponse?.data) setListOfLinguists(linguistsResponse.data);
      if (languagesResponse?.data) {
        const tempLanguages = languagesResponse.data.filter(
          (language) => language.Name !== "ALL",
        );
        setListOfLanguages(tempLanguages);
      }
      if (statusesResponse?.data) setListOfStatuses(statusesResponse.data);
      if (typesResponse?.data) {
        const tempTypes = typesResponse.data.slice(0, 5);
        setListOfTypes(tempTypes);
      }
    } catch (error) {
      console.error("Error fetching overlay data:", error);
      handleErrorDialog(
        "Failed to load assignment creation data. Please try again.",
        theme,
      );
    } finally {
      setIsLoadingOverlayData(false);
    }
  };

  // Fetch overlay data on mount
  useEffect(() => {
    fetchOverlayData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Services cache state: { "2025-11": [...services], "2025-12": [...services] }
  const [servicesCache, setServicesCache] = useState({});
  const [listOfServices, setListOfServices] = useState({});

  // Holidays state: store holidays by date key "YYYY-MM-DD"
  const [holidaysCache, setHolidaysCache] = useState({});
  const [isLoadingHolidays, setIsLoadingHolidays] = useState(false);

  // Track which months are currently being fetched to avoid duplicate requests
  const fetchingMonths = useRef(new Set());

  // Helper: Convert date to month key "YYYY-MM"
  const getMonthKey = (date) => dayjs(date).format("YYYY-MM");

  // Helper: Calculate the range of months needed based on selected month
  // Returns array of month keys: [pastMonth, currentMonth, +3 future months]
  const calculateMonthsToFetch = (referenceDate) => {
    const ref = dayjs(referenceDate);
    const months = [];
    // 1 month back
    months.push(ref.subtract(1, "month").format("YYYY-MM"));
    // Current month
    months.push(ref.format("YYYY-MM"));
    // 3 months ahead
    for (let i = 1; i <= 3; i++) {
      months.push(ref.add(i, "month").format("YYYY-MM"));
    }
    return months;
  };

  // Helper: Fetch services for given month keys
  const fetchServicesForMonths = async (monthKeys, force = false) => {
    // If forcing, clear the fetching tracker for these months
    if (force) {
      monthKeys.forEach((key) => fetchingMonths.current.delete(key));
    }

    const monthsToFetch = force
      ? monthKeys
      : monthKeys.filter(
          (monthKey) =>
            !servicesCache[monthKey] && !fetchingMonths.current.has(monthKey),
        );

    if (monthsToFetch.length === 0) return; // All months already cached

    // Mark months as fetching
    monthsToFetch.forEach((m) => fetchingMonths.current.add(m));

    try {
      // Convert month keys back to month abbreviations and year for API call
      const monthData = monthsToFetch.map((key) => ({
        month: dayjs(key, "YYYY-MM").format("MMM"),
        year: dayjs(key, "YYYY-MM").format("YYYY"),
      }));

      // Group by year and call API
      const byYear = {};
      monthData.forEach(({ month, year }) => {
        if (!byYear[year]) byYear[year] = [];
        byYear[year].push(month);
      });

      // Month abbreviation → numeric month key suffix (e.g. "Dec" → "12")
      const MONTH_NUMBER = {
        Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
        Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
      };

      for (const year of Object.keys(byYear)) {
        const monthsParam = byYear[year].join(",");
        // Month keys covered by THIS year's response — only these may be
        // cleared from the cache. Clearing the whole window per year would
        // wipe the other year's months (e.g. fetching Jan-Mar 2027 deletes
        // Dec 2026 from the cache, so December services disappear).
        const yearMonthKeys = byYear[year].map(
          (monthName) => `${year}-${MONTH_NUMBER[monthName]}`,
        );
        try {
          const response = await api.get("/services/interpretations/brief", {
            params: {
              months: monthsParam,
              year: year,
            },
          });

          if (response?.data) {
            setListOfServices(response.data);

            // Use functional update to avoid race conditions
            setServicesCache((prevCache) => {
              const newCache = { ...prevCache };

              // If forcing, clear only the months this response covers
              if (force) {
                yearMonthKeys.forEach((monthKey) => {
                  delete newCache[monthKey];
                });
              }

              response.data.forEach((service) => {
                const monthKey = getMonthKey(service.Date);
                if (!newCache[monthKey]) {
                  newCache[monthKey] = [];
                }
                // Avoid duplicates (or replace if forcing)
                const existingIndex = newCache[monthKey].findIndex(
                  (s) => s.id === service.id,
                );
                if (existingIndex !== -1) {
                  // Replace existing entry
                  newCache[monthKey][existingIndex] = service;
                } else {
                  // Add new entry
                  newCache[monthKey].push(service);
                }
              });
              return newCache;
            });
          }
        } catch (error) {
          console.error(
            `Error fetching services for ${monthsParam}/${year}:`,
            error,
          );
          handleErrorDialog("Failed to load interpretation services", theme);
        }
      }
    } finally {
      // Mark months as no longer fetching
      monthsToFetch.forEach((m) => fetchingMonths.current.delete(m));
    }
  };

  // Fetch holidays for a date range
  const fetchHolidays = async (startDate, endDate) => {
    if (isLoadingHolidays) return;

    setIsLoadingHolidays(true);
    try {
      const response = await api.get("/holidays", {
        params: {
          startDate: dayjs(startDate).format("YYYY-MM-DD"),
          endDate: dayjs(endDate).format("YYYY-MM-DD"),
          active: true,
        },
      });

      if (response?.data) {
        setHolidaysCache((prevCache) => {
          const newCache = { ...prevCache };
          response.data.forEach((holiday) => {
            const dateKey = dayjs(holiday.Date).format("YYYY-MM-DD");
            // Store an array per date so multiple holidays on the same day
            // all display (dedupe by holiday id on refetch)
            if (!newCache[dateKey]) newCache[dateKey] = [];
            const existingIndex = newCache[dateKey].findIndex(
              (h) => h.id === holiday.id,
            );
            if (existingIndex !== -1) {
              newCache[dateKey][existingIndex] = holiday;
            } else {
              newCache[dateKey].push(holiday);
            }
          });
          return newCache;
        });
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      // Silently fail - holidays are not critical
    } finally {
      setIsLoadingHolidays(false);
    }
  };

  // Get current view reference date
  const viewReferenceDate = useMemo(() => {
    if (selectedDate) return dayjs.tz(selectedDate).toDate();
    if (selectedMonth) return dayjs.tz(selectedMonth).toDate();
    return dayjs.tz().toDate();
  }, [selectedDate, selectedMonth]);

  // Fetch services and holidays when view changes or page refresh is triggered
  useEffect(() => {
    const monthsNeeded = calculateMonthsToFetch(viewReferenceDate);
    // Force refetch when refreshPage changes to ensure fresh data
    fetchServicesForMonths(monthsNeeded, true);

    // Fetch holidays for the view range
    const startDate = dayjs(viewReferenceDate)
      .subtract(1, "month")
      .startOf("month");
    const endDate = dayjs(viewReferenceDate).add(3, "month").endOf("month");
    fetchHolidays(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewReferenceDate, refreshPage]);

  // Listen for assignment updates via socket and trigger refresh
  useEffect(() => {
    if (!socket) {
      return;
    }

    const handleAssignmentSaved = (data) => {
      // Only refresh if it's an Interpretation update
      if (
        data.modelType === "Interpretation" ||
        data.modelType === "InterpretationsList"
      ) {
        // Trigger page refresh which will clear cache and refetch
        triggerPageRefresh();
      }
    };

    socket.on("assignment:saved", handleAssignmentSaved);

    return () => {
      socket.off("assignment:saved", handleAssignmentSaved);
    };
  }, [socket, triggerPageRefresh]);

  // Current time state for indicator line (EST timezone)
  const getESTTime = () => {
    return dayjs().tz("America/New_York");
  };

  const [currentTime, setCurrentTime] = useState(getESTTime());

  // Generate time slots (6 AM to 12 AM in 30-min intervals = 36 slots)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let i = 0; i < 36; i++) {
      const hour = Math.floor(i / 2) + 6;
      const minutes = (i % 2) * 30;
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const timeString = `${displayHour}:${String(minutes).padStart(
        2,
        "0",
      )} ${period}`;
      slots.push(timeString);
    }
    return slots;
  }, []);

  // Generate days for current week (7 days) - updates when selectedDate or selectedMonth changes
  const weekDays = useMemo(() => {
    const days = [];
    let referenceDate = dayjs.tz();

    // Prioritize selectedDate (which contains the selected week's Monday)
    // Fall back to selectedMonth, then current date
    if (selectedDate) {
      referenceDate = dayjs.tz(selectedDate);
    } else if (selectedMonth) {
      referenceDate = dayjs.tz(selectedMonth);
    }

    const monday = dayjs(referenceDate);
    const dayOfWeek = monday.day();
    const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const adjustedMonday = monday.add(offset, "day");

    for (let i = 0; i < 7; i++) {
      const date = adjustedMonday.add(i, "day").toDate();
      const dayName = dayjs(date).format("dddd");
      const dayNum = dayjs(date).date();
      days.push({ date, dayName, dayNum });
    }
    return days;
  }, [selectedDate, selectedMonth]);

  const selectedDayKey = useMemo(
    () => dayjs(selectedDate || dayjs.tz()).format("YYYY-MM-DD"),
    [selectedDate],
  );

  const displayDays = useMemo(() => {
    if (effectiveViewMode === "Week") return weekDays;
    if (effectiveViewMode === "Work Week") return weekDays.slice(0, 5);
    // Day view: show the currently selected date or today
    const ref = selectedDate
      ? dayjs.tz(selectedDate).toDate()
      : dayjs.tz().toDate();
    const dayName = dayjs(ref).format("dddd");
    const dayNum = dayjs(ref).date();
    return [{ date: ref, dayName, dayNum }];
  }, [weekDays, effectiveViewMode, selectedDate]);

  // Flatten cached services into the current display days
  const servicesByDay = useMemo(() => {
    const result = {};
    displayDays.forEach((day) => {
      const key = dayjs(day.date).format("YYYY-MM-DD");
      result[key] = [];
    });

    Object.keys(servicesCache).forEach((monthKey) => {
      const services = servicesCache[monthKey] || [];
      services.forEach((svc) => {
        const svcDay = dayjs(svc.Date).format("YYYY-MM-DD");
        if (result[svcDay]) {
          // Filter based on statusFilters
          const status = svc.InterpretationStatus;
          const shouldInclude = statusFilters.some((filter) => {
            if (filter === "Requested") {
              return status === "Requested";
            } else {
              // For Confirmed, Completed, Cancelled - use startsWith
              return status && status.startsWith(filter);
            }
          });

          if (shouldInclude) {
            result[svcDay].push(svc);
          }
        }
      });
    });
    return result;
  }, [servicesCache, displayDays, statusFilters]);

  // Reset selection when switching weeks
  React.useEffect(() => {
    setSelectedStartSlot(null);
    setSelectedEndSlot(null);
    setIsSelecting(false);
  }, [selectedDate, selectedMonth]);

  // Update current time every minute for time indicator line
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(getESTTime());
    }, 60000); // Update every 60 seconds
    return () => clearInterval(timer);
  }, []);

  // Calculate current time indicator position
  const getTimeIndicatorPosition = () => {
    const now = currentTime;
    const hour = now.hour();
    const minutes = now.minute();

    // Only show if current time is within 6 AM - 12 AM (0 = 6 AM, 36 = 12 AM next day)
    if (hour < 6 || hour > 23) return null;

    // Calculate slot index (2 slots per hour, 6 AM = 0)
    const slotIndex = (hour - 6) * 2 + Math.floor(minutes / 30);
    if (slotIndex < 0 || slotIndex >= 36) return null;

    // Calculate position within the slot (0 to 40px)
    const slotStartMinutes = (hour - 6) * 60 + Math.floor(minutes / 30) * 30;
    const timeMinutes = (hour - 6) * 60 + minutes;
    const offsetInSlot = ((timeMinutes - slotStartMinutes) / 30) * 40;

    return {
      slotIndex,
      offsetInSlot,
    };
  };

  // Check if a date is today (in EST timezone) - matches the currentTime date
  const isTodayDate = (date) => {
    const currentTimeDate = currentTime; // This now includes the +1 day if testing
    return (
      dayjs(date).date() === currentTimeDate.date() &&
      dayjs(date).month() === currentTimeDate.month() &&
      dayjs(date).year() === currentTimeDate.year()
    );
  };

  // ── Scroll position: remember per context, center on now ─────────────────
  // The grid is static (36 slots × 40px, 6 AM → midnight), so the now-line
  // offset is pure geometry. Positioning runs ONLY on mount and after an
  // explicit "today" tap — never on the 60s currentTime tick or period
  // navigation, which must never move the scroll.
  const scrollMemoryKey = `${effectiveViewMode}|${(selectedDate
    ? dayjs.tz(selectedDate)
    : dayjs.tz()
  ).format("YYYY-MM-DD")}`;

  // Latest context key — read at save time (throttled scroll save / unmount),
  // so those writes never capture a stale render's key.
  const scrollMemoryKeyRef = useRef(scrollMemoryKey);
  useEffect(() => {
    scrollMemoryKeyRef.current = scrollMemoryKey;
  });

  const calendarScrollerRef = useRef(null);
  // Latest known scrollTop, kept in sync on every scroll event — the unmount
  // save reads this ref instead of the DOM so it works regardless of ref
  // detach timing.
  const lastScrollTopRef = useRef(0);
  const scrollSaveRafRef = useRef(null);

  // Persist the scroll position, throttled to one write per frame.
  const handleCalendarScroll = () => {
    const element = calendarScrollerRef.current;
    if (!element) return;
    lastScrollTopRef.current = element.scrollTop;
    if (scrollSaveRafRef.current !== null) return;
    scrollSaveRafRef.current = requestAnimationFrame(() => {
      scrollSaveRafRef.current = null;
      CALENDAR_SCROLL_MEMORY.set(
        scrollMemoryKeyRef.current,
        lastScrollTopRef.current,
      );
    });
  };

  // Final save on unmount (service profile navigation, sidebar, route change).
  useEffect(
    () => () => {
      CALENDAR_SCROLL_MEMORY.set(
        scrollMemoryKeyRef.current,
        lastScrollTopRef.current,
      );
    },
    [],
  );

  // Center the now-line mid-screen, clamped at both ends. Skipped when the
  // now-line does not exist (before 6 AM / after 11 PM) or today's column is
  // not displayed (e.g. Work Week over a weekend). Records the result so a
  // later return restores exactly this position.
  const scrollNowLineToMiddle = (element) => {
    if (!element) return;
    if (!displayDays.some((day) => isTodayDate(day.date))) return;
    const indicator = getTimeIndicatorPosition();
    if (!indicator) return;
    const nowY = indicator.slotIndex * 40 + indicator.offsetInSlot;
    const maxScroll = element.scrollHeight - element.clientHeight;
    const target = Math.max(
      0,
      Math.min(nowY - element.clientHeight / 2, maxScroll),
    );
    element.scrollTop = target;
    lastScrollTopRef.current = target;
    CALENDAR_SCROLL_MEMORY.set(scrollMemoryKeyRef.current, target);
  };

  // One-shot position on mount: restore the remembered position for this
  // calendar context (returning from a service profile), otherwise a fresh
  // open centers the now-line. The decision is captured synchronously — a
  // StrictMode double-mount replays the unmount-save cleanup, which must not
  // affect what this effect restores. The guard ref keeps it idempotent, and
  // there is deliberately no cleanup: the scheduled frame must survive the
  // dev remount replay.
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    if (didInitialScrollRef.current) return;
    didInitialScrollRef.current = true;
    const memoryKey = scrollMemoryKeyRef.current;
    const hasMemory = CALENDAR_SCROLL_MEMORY.has(memoryKey);
    const saved = hasMemory ? CALENDAR_SCROLL_MEMORY.get(memoryKey) : 0;
    requestAnimationFrame(() => {
      const element = calendarScrollerRef.current;
      if (!element) return;
      if (hasMemory) {
        const maxScroll = element.scrollHeight - element.clientHeight;
        const target = Math.max(0, Math.min(saved, maxScroll));
        element.scrollTop = target;
        lastScrollTopRef.current = target;
      } else {
        scrollNowLineToMiddle(element);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Explicit "now" intent (today button / today in the mobile day strip):
  // re-center once the tapped day's columns have committed. Period navigation
  // never sets the flag, so arrows just change days without moving the scroll.
  const pendingScrollCenterRef = useRef(false);
  useEffect(() => {
    if (!pendingScrollCenterRef.current) return;
    const raf = requestAnimationFrame(() => {
      pendingScrollCenterRef.current = false;
      scrollNowLineToMiddle(calendarScrollerRef.current);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Check if a date is before today AND within the current week (Mon-Sun)
  const isBeforeTodayInCurrentWeek = (date) => {
    const dt = dayjs(date);
    const now = currentTime;
    const monday = dayjs(now).startOf("week").add(1, "day"); // Monday as start
    const nextMonday = monday.add(7, "day");

    const beforeToday = dt.isBefore(dayjs(now), "day");
    const inThisWeek =
      dt.isSame(monday, "day") ||
      (dt.isAfter(monday, "day") && dt.isBefore(nextMonday, "day"));
    return beforeToday && inThisWeek;
  };

  const isPastDate = (date) => dayjs(date).isBefore(dayjs(currentTime), "day");

  const getDayColumnBackgroundColor = (date) =>
    isPastDate(date)
      ? theme.palette.grey[200]
      : theme.palette.background.default;

  // Compute layout for overlapping services in a single day
  const layoutDayServices = (services) => {
    // Grid bounds: 6 AM (0) to 12 AM next day (36 slots x 30 min = 1080)
    const refStart = 6 * 60;
    const gridEndMinutes = timeSlots.length * 30;

    // Map services to time ranges in minutes from 6:00 AM
    const withTimes = services
      .map((svc, idx) => {
        const start = dayjs(`${svc.Date} ${svc.TimeStart}`);
        const end = dayjs(`${svc.Date} ${svc.TimeEnd}`);
        const startMinutes = start.hour() * 60 + start.minute();
        const endMinutes = end.hour() * 60 + end.minute();
        // Overnight assignment (TimeEnd < TimeStart, e.g. 10 PM -> 2 AM):
        // the block spans from TimeStart to the end of the grid (12 AM) and
        // continues past midnight. The real end time stays on svc.TimeEnd and
        // is shown in the expanded view / tooltip.
        const isOvernight = endMinutes < startMinutes;
        const topMinutes = Math.max(0, startMinutes - refStart);
        const bottomMinutes = isOvernight
          ? gridEndMinutes
          : Math.max(topMinutes + 30, endMinutes - refStart); // ensure minimum 30 minutes
        return {
          ...svc,
          _start: topMinutes,
          _end: bottomMinutes,
          _overnight: isOvernight,
          _idx: idx,
        };
      })
      .sort((a, b) => a._start - b._start || a._end - b._end);

    if (withTimes.length === 0) return [];

    // Build overlap graph - determine which events overlap with which
    const overlaps = Array(withTimes.length)
      .fill(null)
      .map(() => []);
    for (let i = 0; i < withTimes.length; i++) {
      for (let j = i + 1; j < withTimes.length; j++) {
        // Check if i and j overlap
        if (
          withTimes[i]._start < withTimes[j]._end &&
          withTimes[i]._end > withTimes[j]._start
        ) {
          overlaps[i].push(j);
          overlaps[j].push(i);
        }
      }
    }

    // Find clusters using BFS - group events that overlap transitively
    const visited = Array(withTimes.length).fill(false);
    const clusters = [];

    for (let i = 0; i < withTimes.length; i++) {
      if (!visited[i]) {
        const cluster = [];
        const queue = [i];
        visited[i] = true;

        while (queue.length > 0) {
          const current = queue.shift();
          cluster.push(current);

          for (const neighbor of overlaps[current]) {
            if (!visited[neighbor]) {
              visited[neighbor] = true;
              queue.push(neighbor);
            }
          }
        }

        clusters.push(cluster);
      }
    }

    // For each cluster, assign columns
    const layouts = [];
    for (let clusterIdx = 0; clusterIdx < clusters.length; clusterIdx++) {
      const clusterIndices = clusters[clusterIdx];
      const clusterServices = clusterIndices.map((idx) => withTimes[idx]);

      // Sort cluster services by start time
      clusterServices.sort((a, b) => a._start - b._start || a._end - b._end);

      // Assign columns using sweep-line algorithm
      for (const svc of clusterServices) {
        // Find services that actually overlap with svc in time
        const actualOverlaps = clusterServices.filter(
          (other) =>
            other !== svc && other._start < svc._end && other._end > svc._start,
        );

        // Among actual overlaps, get those that are already assigned
        const assignedOverlaps = actualOverlaps.filter(
          (o) => o._col !== undefined,
        );
        const usedColumns = assignedOverlaps.map((o) => o._col);

        // Assign to first available column
        let col = 0;
        while (usedColumns.includes(col)) col++;

        svc._col = col;
      }

      // Calculate max columns for this cluster
      const maxCols = Math.max(...clusterServices.map((s) => s._col), -1) + 1;

      // Calculate colSpan for each service
      for (const svc of clusterServices) {
        // Find services that overlap with svc (at its time window)
        const overlappingAtSvcTime = clusterServices.filter(
          (other) =>
            other !== svc && other._start < svc._end && other._end > svc._start,
        );

        if (overlappingAtSvcTime.length === 0) {
          // No overlaps during this service's time, use single column
          svc._colSpan = 1;
        } else {
          // Has overlaps - find which columns are occupied
          const occupiedCols = overlappingAtSvcTime.map((o) => o._col);
          const minOccupiedCol = Math.min(...occupiedCols);
          const maxOccupiedCol = Math.max(...occupiedCols);

          // If this service is before all occupied columns, expand to reach them
          if (svc._col < minOccupiedCol) {
            svc._colSpan = minOccupiedCol - svc._col;
          } else if (svc._col > maxOccupiedCol) {
            // This service is after all occupied columns, can span to the end
            svc._colSpan = maxCols - svc._col;
          } else if (svc._col >= minOccupiedCol && svc._col <= maxOccupiedCol) {
            // This service is within the occupied zone
            // Try to expand right to fill free columns
            let colsToSpan = 1;
            for (let c = svc._col + 1; c < maxCols; c++) {
              // Check if column c is occupied during this service's time
              const isOccupied = occupiedCols.includes(c);
              if (!isOccupied) {
                colsToSpan++;
              } else {
                break; // Stop at first occupied column
              }
            }
            svc._colSpan = colsToSpan;
          }
        }
      }

      // Recalculate maxCols considering spans
      const finalMaxCols = Math.max(
        ...clusterServices.map((s) => s._col + s._colSpan),
        1,
      );

      // Add to layouts
      for (const svc of clusterServices) {
        layouts.push({
          svc,
          col: svc._col,
          colSpan: svc._colSpan,
          totalCols: finalMaxCols,
          clusterId: clusterIdx,
        });
      }
    }

    // Sort layouts back to original service order for consistency
    layouts.sort((a, b) => a.svc._idx - b.svc._idx);

    return layouts.map((item) => ({
      svc: item.svc,
      col: item.col,
      colSpan: item.colSpan,
      totalCols: item.totalCols,
    }));
  };

  // Navigate to previous period
  const handlePreviousPeriod = () => {
    // Respect the selected month when no specific date is selected —
    // otherwise arrows jump back to today after picking a future month.
    const currentDate = selectedDate
      ? dayjs.tz(selectedDate)
      : selectedMonth
        ? dayjs.tz(selectedMonth)
        : dayjs.tz();
    if (effectiveViewMode === "Day") {
      const prevDate = currentDate.subtract(1, "day").toDate();
      setSelectedDate(prevDate);
    } else {
      // For Week and Work Week, go back 7 days
      const prevDate = currentDate.subtract(7, "day").toDate();
      setSelectedDate(prevDate);
    }
  };

  // Navigate to next period
  const handleNextPeriod = () => {
    // Respect the selected month when no specific date is selected —
    // otherwise arrows jump back to today after picking a future month.
    const currentDate = selectedDate
      ? dayjs.tz(selectedDate)
      : selectedMonth
        ? dayjs.tz(selectedMonth)
        : dayjs.tz();
    if (effectiveViewMode === "Day") {
      const nextDate = currentDate.add(1, "day").toDate();
      setSelectedDate(nextDate);
    } else {
      // For Week and Work Week, go forward 7 days
      const nextDate = currentDate.add(7, "day").toDate();
      setSelectedDate(nextDate);
    }
  };

  // Go to today or current week
  const handleGoToToday = () => {
    // Explicit "now" intent — re-centers the grid once today's view commits.
    pendingScrollCenterRef.current = true;
    setSelectedDate(null); // Setting to null will make it default to today
  };

  // Mobile day strip: tapping today's card is an explicit "go to now"; any
  // other day just switches the view (and must not inherit a stale re-center).
  const handleMobileDayTap = (date) => {
    pendingScrollCenterRef.current = isTodayDate(date);
    setSelectedDate(date);
  };

  // Check if we're currently viewing today
  const isViewingToday = () => {
    const today = dayjs.tz().startOf("day");

    if (selectedDate) {
      const selected = dayjs.tz(selectedDate).startOf("day");
      return selected.isSame(today, "day");
    }

    // If no selectedDate, we're viewing today
    return true;
  };

  // Context menu handlers
  const handleContextMenu = (event, service) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedRow(service);
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  const handleContextMenuEdit = () => {
    api
      .get(`/services/interpretations/${selectedRow.id}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_EDIT,
          Interpretations: listOfServices,
          Clients: listOfClients,
          ClientsOPIMinimums: listOfClientsOPIMinimums,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        handleContextMenuClose();
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow.id,
        ServiceType: "Interpretation",
      },
      Service: {
        id: selectedRow.id,
        ProjectNumber: selectedRow.ProjectNumber,
        Details: selectedRow.Details,
        ServiceCategory: selectedRow.ServiceCategory,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    handleContextMenuClose();
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/interpretations/${selectedRow.id}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE,
          Interpretations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        handleContextMenuClose();
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  // Open an assignment's Outlook email thread (2026-10-06/07): the menu
  // items only appear when the assignment has saved links; the full links
  // are rebuilt from the stored ids. Multiple links get numbered entries.
  const handleContextMenuOpenEmailThread = (emailThreadId) => {
    if (emailThreadId) {
      window.open(
        buildEmailThreadLink(emailThreadId),
        "_blank",
        "noopener,noreferrer",
      );
    }
    handleContextMenuClose();
  };

  // Copy actions (2026-10-07): copy a thread's full link or the assignment's
  // project number to the clipboard, mirroring the portal's copy pattern.
  const handleContextMenuCopyEmailThreadLink = (emailThreadId) => {
    if (emailThreadId) {
      navigator.clipboard.writeText(buildEmailThreadLink(emailThreadId));
      showSnackbar("Email thread link copied to clipboard.", theme, "info");
    }
    handleContextMenuClose();
  };

  const handleContextMenuCopyProjectNumber = () => {
    if (selectedRow?.ProjectNumber) {
      navigator.clipboard.writeText(String(selectedRow.ProjectNumber));
      showSnackbar("Project number copied to clipboard.", theme, "info");
    }
    handleContextMenuClose();
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Compute selected range details (date + start/end times) - Memoized to avoid recalculation
  const computeSelectedRange = useMemo(() => {
    if (!selectedStartSlot || !selectedEndSlot) return null;

    const dayIdx = selectedStartSlot.day;
    const dateObj = displayDays?.[dayIdx]?.date;
    if (!dateObj) return null;

    const baseMinutes = 6 * 60; // 6:00 AM
    const startIdx = Math.min(selectedStartSlot.slot, selectedEndSlot.slot);
    const endIdxExclusive =
      Math.max(selectedStartSlot.slot, selectedEndSlot.slot) + 1; // exclusive end

    const startTotal = baseMinutes + startIdx * 30;
    const endTotal = baseMinutes + endIdxExclusive * 30;

    const toDisplay12 = (total) => {
      let h = Math.floor(total / 60);
      const m = total % 60;
      const period = h >= 12 ? "PM" : "AM";
      const displayHour = h % 12 === 0 ? 12 : h % 12;
      return `${displayHour}:${String(m).padStart(2, "0")} ${period}`;
    };

    const dateISO = dayjs(dateObj).format("YYYY-MM-DD");
    const dateDisplay = dayjs(dateObj).format("MM, ddd DD, YYYY"); // MM, EEE DD, YYYY

    const startH = Math.floor(startTotal / 60);
    const startM = startTotal % 60;
    let endH = Math.floor(endTotal / 60);
    let endM = endTotal % 60;

    // Create UTC dates to prevent timezone shifting
    // Use the ISO date string and parse it as UTC
    const dateUTC = dayjs.utc(dateISO);

    const startJS = dateUTC
      .hour(startH)
      .minute(startM)
      .second(0)
      .millisecond(0);
    let endJS = dateUTC.hour(endH).minute(endM).second(0).millisecond(0);
    // If selection reaches 24:00, advance to next day 00:00
    if (endH >= 24) {
      endJS = dateUTC.add(1, "day").startOf("day");
      endH = 0;
      endM = 0;
    }

    const toHHmm = (h, m) =>
      `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

    return {
      dateISO,
      dateDisplay,
      startHHmm: toHHmm(startH, startM),
      endHHmm: toHHmm(endH, endM),
      startDisplay: toDisplay12(startTotal),
      endDisplay: toDisplay12(endTotal),
      dateJS: dateUTC,
      startJS,
      endJS,
      dateObj,
    };
  }, [selectedStartSlot, selectedEndSlot, displayDays]);

  // Handle creating an assignment from selection
  const handleCreateAssignment = () => {
    if (!selectedStartSlot || !selectedEndSlot) return;

    const range = computeSelectedRange;

    const newProjectNumber = getProjectNumber(range?.dateObj, listOfServices);

    const data = {
      ProjectNumber: newProjectNumber,
      Clients: listOfClients,
      ClientsOPIMinimums: listOfClientsOPIMinimums,
      Linguists: listOfLinguists,
      Languages: listOfLanguages,
      Interpretations: [],
      InterpretationStatuses: listOfStatuses,
      InterpretationTypes: listOfTypes,
      Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
      PrefilledData: {
        Date: range?.dateJS,
        TimeStart: range?.startJS,
        TimeEnd: range?.endJS,
      },
    };

    setOverlayData(data);
    setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
    setSelectedStartSlot(null);
    setSelectedEndSlot(null);
  };

  // Status-driven color palette for service cards
  const getStatusColors = (status) => {
    switch (status) {
      case "Requested":
        return {
          main: theme.palette.error.main,
          bg:
            theme.palette.mode === "dark"
              ? theme.palette.error[800]
              : theme.palette.error[100],
          shadow: theme.palette.error[700],
        };
      case "Confirmed":
        return {
          main: theme.palette.info.main,
          bg:
            theme.palette.mode === "dark"
              ? theme.palette.info[800]
              : theme.palette.info[100],
          shadow: theme.palette.info[700],
        };
      case "Completed":
        return {
          main: theme.palette.success.main,
          bg:
            theme.palette.mode === "dark"
              ? theme.palette.success[800]
              : theme.palette.success[100],
          shadow: theme.palette.success[700],
        };
      default:
        return {
          main: theme.palette.grey[500],
          bg:
            theme.palette.grey[100],
          shadow: theme.palette.grey[700],
        };
    }
  };

  const navigate = useNavigate();

  // Helper: Open service profile
  const handleOpenServiceProfile = (service) => {
    if (!service?.id) return;

    let path = "";
    // Determine service type - these are all interpretations from the calendar
    const serviceType = service.ServiceType || "Interpretation";

    if (serviceType === "Translation") {
      path = `/translations/${service.id}`;
    } else if (serviceType === "Interpretation") {
      path = `/interpretations/${service.id}`;
    } else if (serviceType === "Other Service") {
      path = `/services/${service.id}`;
    }

    if (path) {
      setOverlayName("");
      // Remember where the user was before the Calendar unmounts — returning
      // from the profile restores this position instead of re-centering.
      CALENDAR_SCROLL_MEMORY.set(
        scrollMemoryKeyRef.current,
        lastScrollTopRef.current,
      );
      navigate(path, { state: { ComingFrom: "/calendar" } });
    }
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isSmallScreen ? 0 : 20)}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, gap: 1 }}
          >
            {/* Phones (xs only — same <600px boundary as `isMobile` below,
                which switches both this file's sync button and
                CalendarFilterSelector to their 40×40 icon-button form): the
                wrapper must NOT claim 100% width here, or the admin Verify
                Sync button wraps onto its own row. It shrinks instead, so the
                two 40px icons + the container's 8px gap sit on one row
                (fits easily at 320px). From sm up the layout is untouched
                (width 100% up to 300px, flex 0 1 auto). */}
            <Box
              sx={{
                width: { xs: "auto", sm: "100%" },
                maxWidth: FILTER_SELECTOR_WIDTH,
                minWidth: 0,
                flex: { xs: "1 1 0%", sm: "0 1 auto" },
              }}
            >
              <CalendarFilterSelector
                selectedDate={selectedDate}
                selectedMonth={selectedMonth}
                onDateChange={setSelectedDate}
                onMonthChange={setSelectedMonth}
                viewMode={effectiveViewMode}
                onViewModeChange={handleViewModeChange}
                statusFilters={statusFilters}
                onStatusFiltersChange={setStatusFilters}
              />
            </Box>

            {/* Verify Outlook Sync — admin only; opens the check/sync overlay.
                Styled to match the "Add Interpretation" button (2026-09-23,
                per Amine): solid primary, 12px radius, soft shadow, centered
                label with the icon pinned left. Phones (xs): the same action
                renders as the portal's 40×40 icon button with a tooltip. */}
            {isAdmin &&
              (isMobile ? (
                <Tooltip title="Verify Sync">
                  <Button
                    onClick={() =>
                      setOverlayName(OVERLAY_NAMES.CALENDAR_SYNC_VERIFY)
                    }
                    aria-label="Verify Outlook Sync"
                    sx={{
                      width: "40px",
                      minWidth: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      color: theme.palette.background.default,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "& .MuiButton-startIcon": {
                        margin: 0,
                      },
                      "& .MuiButton-endIcon": {
                        display: "none",
                      },
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                        color: theme.palette.background.default,
                        boxShadow:
                          "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <SyncRoundedIcon />
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  onClick={() =>
                    setOverlayName(OVERLAY_NAMES.CALENDAR_SYNC_VERIFY)
                  }
                  startIcon={<SyncRoundedIcon />}
                  sx={{
                    width: "200px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>
                    {isSmallScreen ? "Verify Sync" : "Verify Outlook Sync"}
                  </Box>
                </Button>
              ))}
          </Grid>
        </Grid>
      </Box>

      {/* Calendar Container */}
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          boxShadow: theme.shadows[3],
          height: "100%",
          display: "flex",
          flexDirection: "column",
          //marginTop: "0.5rem",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
        }}
      >
        {/* Day Headers Row - Fixed Top */}
        <Box
          sx={{
            display: "grid",
            mr: ".45rem",
            gridTemplateColumns: isSmallScreen
              ? "90px 1fr"
              : `90px repeat(${displayDays.length}, 1fr)`,
            flexShrink: 0,
          }}
        >
          {/* Corner spacer with navigation buttons */}
          <Box
            sx={{
              height: "60px",
              backgroundColor: theme.palette.background.default,
              borderRight: `2px solid ${theme.palette.divider}`,
              borderBottom: `2px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              //gap: 0.5,
              //px: 0.5,
            }}
          >
            <IconButton
              size="small"
              onClick={handlePreviousPeriod}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ChevronLeftRounded fontSize="small" />
            </IconButton>
            {!isViewingToday() && (
              <IconButton
                size="small"
                onClick={handleGoToToday}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <AdjustRoundedIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleNextPeriod}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ChevronRightRounded fontSize="small" />
            </IconButton>
          </Box>
          {isSmallScreen ? (
            <Box
              sx={{
                borderLeft: `2px solid ${theme.palette.divider}`,
                height: "60px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                //my: 1,
                overflowX: "auto",
                backgroundColor: theme.palette.background.default,
                borderRight: `2px solid ${theme.palette.divider}`,
                borderBottom: `2px solid ${theme.palette.divider}`,
              }}
            >
              {weekDays.slice(0, 5).map((day, dayIndex) => {
                const dayKey = dayjs(day.date).format("YYYY-MM-DD");
                const isSelected = dayKey === selectedDayKey;
                const dayNameShort = day.dayName.substring(0, 3);

                return (
                  <Box
                    key={`day-selector-${dayIndex}`}
                    onClick={() => handleMobileDayTap(day.date)}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      height: 45,
                      fontWeight: 600,
                      borderRadius: "10px",
                      padding: "4px",
                      cursor: "pointer",
                      border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                      backgroundColor: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.background.default,
                      position: "relative",
                      //,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1,
                        color: isSelected
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                      }}
                    >
                      {day.dayNum}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1,
                        color: isSelected
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                      }}
                    >
                      {dayNameShort}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            displayDays.map((day, dayIndex) => (
              <Box
                key={`day-header-${dayIndex}`}
                sx={{
                  borderLeft: `2px solid ${theme.palette.divider}`,

                  height: "60px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: theme.palette.background.default,
                  padding: "8px",
                  borderRight: `2px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${
                    isTodayDate(day.date)
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  position: "relative",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  }}
                >
                  {day.dayNum}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {day.dayName}
                </Typography>

                <HtmlTooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      {(() => {
                        const dayServices =
                          servicesByDay[dayjs(day.date).format("YYYY-MM-DD")] ||
                          [];
                        const totalCount = dayServices.length;
                        const requestedCount = dayServices.filter(
                          (s) => s.InterpretationStatus === "Requested",
                        ).length;
                        const confirmedCount = dayServices.filter(
                          (s) => s.InterpretationStatus === "Confirmed",
                        ).length;
                        const completedCount = dayServices.filter(
                          (s) => s.InterpretationStatus === "Completed",
                        ).length;

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                Total: {totalCount}
                              </Typography>
                              <Typography variant="body2">
                                Requested: {requestedCount}
                              </Typography>
                              <Typography variant="body2">
                                Confirmed: {confirmedCount}
                              </Typography>
                              <Typography variant="body2">
                                Completed: {completedCount}
                              </Typography>
                            </Box>

                            {dayServices.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.75,
                                  pointerEvents: "auto",
                                }}
                              >
                                <Divider sx={{ my: 0.5 }} />
                                {dayServices.map((service) => (
                                  <Box
                                    key={service.id}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.85rem" }}
                                    >
                                      {service.ProjectNumber ||
                                        service.Name ||
                                        "Service"}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() =>
                                        handleOpenServiceProfile(service)
                                      }
                                      sx={{
                                        fontSize: "0.75rem",
                                        padding: "3px 8px",
                                        borderRadius: "4px",
                                        pointerEvents: "auto",
                                        cursor: "pointer",
                                      }}
                                    >
                                      View
                                    </Button>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  }
                >
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      bottom: "8px",
                      right: "8px",
                      fontWeight: 600,
                      color: theme.palette.primary.text2,
                      cursor: "pointer",
                    }}
                  >
                    {
                      (
                        servicesByDay[dayjs(day.date).format("YYYY-MM-DD")] ||
                        []
                      ).length
                    }
                  </Typography>
                </HtmlTooltip>
              </Box>
            ))
          )}
        </Box>

        {/* Holiday Bar Row - For Mobile (Day view only) */}
        {isSmallScreen &&
          (() => {
            const selectedDay = displayDays[0];
            const dayKey = dayjs(selectedDay.date).format("YYYY-MM-DD");
            const holidays = holidaysCache[dayKey] || [];
            const holiday = holidays[0];

            return (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "90px 1fr",
                  height: holiday ? "25px" : "0px",
                  mr: ".45rem",
                  flexShrink: 0,
                  overflow: "hidden",
                  transition: holiday
                    ? "height 0.3s ease-in-out, opacity 0.3s ease-in-out"
                    : "opacity 0.2s ease-in-out, height 0.3s ease-in-out 0.1s",
                  opacity: holiday ? 1 : 0,
                }}
              >
                {/* Empty corner cell */}
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    borderRight: `2px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                />
                {/* Holiday cell */}
                <Box
                  sx={{
                    borderLeft: `2px solid ${theme.palette.divider}`,
                    borderRight: `2px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    backgroundColor: holiday
                      ? alpha(theme.palette.info.main, 0.08)
                      : theme.palette.background.default,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1,
                    minWidth: 0,
                  }}
                >
                  {holiday && (
                    <HtmlTooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          {holidays.map((h) => (
                            <Box key={h.id} sx={{ mb: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {h.Name}
                              </Typography>
                              {h.Description && (
                                <Typography
                                  variant="caption"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  {h.Description}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      }
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.info.main,
                          fontSize: "0.7rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        {holiday.Name}
                      </Typography>
                    </HtmlTooltip>
                  )}
                </Box>
              </Box>
            );
          })()}

        {/* Holiday Bar Row - For Desktop (Week/Work Week view) */}
        {!isSmallScreen &&
          (() => {
            // Check if any day in the week has a holiday
            const hasHolidayInWeek = displayDays.some((day) => {
              const dayKey = dayjs(day.date).format("YYYY-MM-DD");
              return (holidaysCache[dayKey] || []).length > 0;
            });

            return (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `90px repeat(${displayDays.length}, 1fr)`,
                  height: hasHolidayInWeek ? "25px" : "0px",
                  mr: ".45rem",
                  flexShrink: 0,
                  overflow: "hidden",
                  transition: hasHolidayInWeek
                    ? "height 0.3s ease-in-out, opacity 0.3s ease-in-out"
                    : "opacity 0.2s ease-in-out, height 0.3s ease-in-out 0.1s",
                  opacity: hasHolidayInWeek ? 1 : 0,
                }}
              >
                {/* Empty corner cell */}
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    borderRight: `2px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                />
                {/* Holiday cells for each day */}
                {displayDays.map((day, dayIndex) => {
                  const dayKey = dayjs(day.date).format("YYYY-MM-DD");
                  const holidays = holidaysCache[dayKey] || [];
                  const holiday = holidays[0];

                  return (
                    <Box
                      key={`holiday-${dayIndex}`}
                      sx={{
                        borderLeft: `2px solid ${theme.palette.divider}`,
                        borderRight: `2px solid ${theme.palette.divider}`,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        backgroundColor: holiday
                          ? alpha(theme.palette.info.main, 0.08)
                          : theme.palette.background.default,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 0.5,
                        minWidth: 0,
                      }}
                    >
                      {holiday && (
                        <HtmlTooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              {holidays.map((h) => (
                                <Box key={h.id} sx={{ mb: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {h.Name}
                                  </Typography>
                                  {h.Description && (
                                    <Typography
                                      variant="caption"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      {h.Description}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          }
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.info.main,
                              fontSize: "0.7rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              display: "block",
                              width: "100%",
                              textAlign: "center",
                            }}
                          >
                            {holiday.Name}
                          </Typography>
                        </HtmlTooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })()}

        {/* Scrollable Container - Time Column + Grid */}
        <Box
          ref={calendarScrollerRef}
          onScroll={handleCalendarScroll}
          onMouseUp={(e) => {
            setIsSelecting(false);
            if (selectedStartSlot !== null && selectedEndSlot !== null) {
              setPopoverAnchorEl(
                e.target instanceof HTMLElement ? e.target : null,
              );
            }
          }}
          onMouseLeave={() => setIsSelecting(false)}
          sx={{
            flex: 1,
            display: "flex",
            overflow: "auto",
            borderTop: `2px solid ${theme.palette.divider}`,
            scrollbarGutter: "stable",
          }}
        >
          {/* Time Column */}
          <Box
            sx={{
              width: "90px",
              flexShrink: 0,
              height: "fit-content",
              borderRight: `2px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Time Labels */}
            {timeSlots.map((time, index) => (
              <Box
                key={`time-${index}`}
                sx={{
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  paddingY: "2px",
                  flexShrink: 0,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.7rem",
                    textAlign: "center",
                  }}
                >
                  {time}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Time Slots Grid - Scrollable */}
          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: `repeat(${displayDays.length}, 1fr)`,
              height: "fit-content",
              borderLeft: `2px solid ${theme.palette.divider}`,
            }}
          >
            {/* Day Columns */}
            {displayDays.map((day, dayIndex) => {
              const timeIndicator = getTimeIndicatorPosition();
              const isToday = isTodayDate(day.date);
              const isBeforeToday = isBeforeTodayInCurrentWeek(day.date);
              const showIndicator = (isToday || isBeforeToday) && timeIndicator;
              const dow = dayjs(day.date).day();
              const isWeekendColumn = dow === 0 || dow === 6;
              const dayColumnBackgroundColor = getDayColumnBackgroundColor(
                day.date,
              );
              const dayKey = dayjs(day.date).format("YYYY-MM-DD");
              const dayServices = servicesByDay[dayKey] || [];
              const laidOut = layoutDayServices(dayServices);

              return (
                <Box
                  key={`day-${dayIndex}`}
                  sx={{
                    backgroundColor: dayColumnBackgroundColor,
                    borderRight: `2px solid ${theme.palette.divider}`,
                    position: "relative",
                  }}
                >
                  {/* Time Indicator Line */}
                  {showIndicator && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: `${
                          timeIndicator.slotIndex * 40 +
                          timeIndicator.offsetInSlot
                        }px`,
                        left: 0,
                        right: 0,
                        height: isToday ? "2px" : "1px",
                        backgroundColor: isToday
                          ? theme.palette.primary.main
                          : "transparent",
                        backgroundImage: isBeforeToday
                          ? `repeating-linear-gradient(90deg, ${theme.palette.primary.main} 0 8px, transparent 8px 16px)`
                          : "none",
                        backgroundRepeat: "repeat-x",
                        backgroundPosition: "left center",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        // Center the line on the exact time by offsetting half its thickness
                        transform: isToday
                          ? "translateY(-1px)"
                          : "translateY(-0.5px)",
                      }}
                    >
                      {/* Rounded square indicator on the left - only for today */}
                      {isToday && (
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: "2px",
                            marginLeft: "-4px",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Time Slots Grid for this day */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateRows: `repeat(${timeSlots.length}, 40px)`,
                      backgroundColor: dayColumnBackgroundColor,
                    }}
                  >
                    {timeSlots.map((time, slotIndex) => {
                      const slotId = `${dayIndex}-${slotIndex}`;
                      const isSelected =
                        selectedStartSlot && selectedEndSlot
                          ? slotIndex >=
                              Math.min(
                                selectedStartSlot.slot,
                                selectedEndSlot.slot,
                              ) &&
                            slotIndex <=
                              Math.max(
                                selectedStartSlot.slot,
                                selectedEndSlot.slot,
                              ) &&
                            dayIndex === selectedStartSlot.day
                          : false;
                      const isHalfHour = slotIndex % 2 === 1;

                      return (
                        <TimeSlotCell
                          key={slotId}
                          slotId={slotId}
                          isSelected={isSelected}
                          onMouseDown={() => {
                            // Check if clicking on already selected single cell - deselect
                            if (
                              selectedStartSlot &&
                              selectedEndSlot &&
                              selectedStartSlot.day === dayIndex &&
                              selectedStartSlot.slot === slotIndex &&
                              selectedEndSlot.day === dayIndex &&
                              selectedEndSlot.slot === slotIndex
                            ) {
                              setSelectedStartSlot(null);
                              setSelectedEndSlot(null);
                              setIsSelecting(false);
                            } else {
                              // Start new selection
                              setIsSelecting(true);
                              setSelectedStartSlot({
                                day: dayIndex,
                                slot: slotIndex,
                              });
                              setSelectedEndSlot({
                                day: dayIndex,
                                slot: slotIndex,
                              });
                            }
                          }}
                          onMouseEnter={() => {
                            setSelectedEndSlot({
                              day: dayIndex,
                              slot: slotIndex,
                            });
                          }}
                          theme={theme}
                          isSelecting={isSelecting}
                          isHalfHour={isHalfHour}
                          isWeekend={isWeekendColumn}
                          columnBackgroundColor={dayColumnBackgroundColor}
                        />
                      );
                    })}
                  </Box>

                  {/* Services overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      pointerEvents: "none",
                      zIndex: 5,
                    }}
                  >
                    {(() => {
                      return laidOut.map(
                        ({ svc, col, colSpan, totalCols }, idx) => {
                          const statusColors = getStatusColors(
                            svc.InterpretationStatus,
                          );
                          const typeInfo = getInterpretationTypeInfo(svc);

                          // Calculate position in pixels
                          const topPx = (svc._start / 30) * 40; // 30 min = 40px
                          const heightPx = ((svc._end - svc._start) / 30) * 40;

                          // Calculate left and width based on column position and span
                          const leftPercent = (col / totalCols) * 100;
                          const widthPercent = (colSpan / totalCols) * 100;

                          // Format time display
                          const isOvernight = !!svc._overnight;
                          const startHour = 6 + Math.floor(svc._start / 60);
                          const startMin = svc._start % 60;
                          // Overnight blocks end at the grid end (12 AM); the
                          // real end time (next day) is preserved on svc.TimeEnd.
                          const endHour = isOvernight
                            ? 24
                            : 6 + Math.floor(svc._end / 60);
                          const endMin = isOvernight ? 0 : svc._end % 60;

                          const toAMPM = (hour, min) => {
                            const normalizedHour = hour % 24;
                            const period = normalizedHour >= 12 ? "PM" : "AM";
                            const displayHour =
                              normalizedHour > 12
                                ? normalizedHour - 12
                                : normalizedHour === 0
                                  ? 12
                                  : normalizedHour;
                            return `${displayHour}:${String(min).padStart(2, "0")} ${period}`;
                          };

                          const startTime = toAMPM(startHour, startMin);
                          const endTime = toAMPM(endHour, endMin);
                          // Real end time for overnight assignments (next day)
                          const realEnd = dayjs(`${svc.Date} ${svc.TimeEnd}`);
                          const realEndTime = isOvernight
                            ? `${toAMPM(realEnd.hour(), realEnd.minute())} (+1 day)`
                            : endTime;

                          // Multi-timezone conversion lines for Client/Linguist
                          // non-EST zones ("Client: 7:00 AM - 8:00 AM PDT", ...).
                          const multiTzLines = buildMultiTimezoneLines({
                            timeStart: svc?.TimeStart,
                            timeEnd: svc?.TimeEnd,
                            date: svc?.Date,
                            clientTimezone: svc?.Client?.Timezone,
                            linguistTimezones: svc?.Linguist?.Timezone
                              ? [svc.Linguist.Timezone]
                              : [],
                          });
                          const tooltipKey = `svc-${svc.id}`;
                          const isServiceHovered =
                            !isSmallScreen && hoveredServiceKey === tooltipKey;
                          const isServiceExpanded = isSmallScreen
                            ? expandedMobileServiceKey === tooltipKey
                            : isServiceHovered;
                          const isDesktopHoverExpanded =
                            !isSmallScreen && isServiceHovered;
                          const collapsedHeightPx = Math.max(heightPx - 8, 36);
                          const expandedMinHeightPx = Math.max(
                            collapsedHeightPx + 84,
                            160,
                          );

                          return (
                            <HtmlTooltip
                              key={`svc-${svc.id}-${idx}`}
                              placement="right"
                              open={false}
                              disableHoverListener
                              disableFocusListener
                              disableTouchListener
                              title=""
                            >
                              <Box
                                onMouseEnter={() => {
                                  if (!isSmallScreen) {
                                    setHoveredServiceKey(tooltipKey);
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (!isSmallScreen) {
                                    setHoveredServiceKey(null);
                                  }
                                }}
                                onClick={() => {
                                  if (isSmallScreen) {
                                    if (
                                      expandedMobileServiceKey === tooltipKey
                                    ) {
                                      handleOpenServiceProfile(svc);
                                    } else {
                                      setExpandedMobileServiceKey(tooltipKey);
                                    }
                                  }
                                }}
                                onContextMenu={(e) => handleContextMenu(e, svc)}
                                sx={{
                                  position: "absolute",
                                  top: `${topPx}px`,
                                  left: isDesktopHoverExpanded
                                    ? "4px"
                                    : `${leftPercent}%`,
                                  height: isServiceExpanded
                                    ? "auto"
                                    : `${collapsedHeightPx}px`,
                                  minHeight: isServiceExpanded
                                    ? `${expandedMinHeightPx}px`
                                    : `${collapsedHeightPx}px`,
                                  width: isDesktopHoverExpanded
                                    ? "calc(100% - 8px)"
                                    : `calc(${widthPercent}% - 8px)`,
                                  m: "4px",
                                  pointerEvents: "auto",
                                  borderBottom: `3px solid ${statusColors.main}`,
                                  bgcolor: statusColors.bg,
                                  boxShadow: `
                                  0px 3px 5px -1px ${alpha(
                                    statusColors.shadow,
                                    0.2,
                                  )},
                                  0px 6px 10px 0px ${alpha(
                                    statusColors.shadow,
                                    0.14,
                                  )},
                                  0px 1px 18px 0px ${alpha(
                                    statusColors.shadow,
                                    0.12,
                                  )}
                                `,
                                  borderRadius: "8px",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  px: 0.5,
                                  py: 0.5,
                                  cursor: "pointer",
                                  overflow: isServiceExpanded
                                    ? "visible"
                                    : "hidden",
                                  zIndex: isServiceExpanded ? 35 : 6,
                                  transition:
                                    "left 0.2s ease, width 0.2s ease, min-height 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                                  transform: isServiceHovered
                                    ? "scale(1.02)"
                                    : "scale(1)",
                                }}
                              >
                                {isOvernight && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 2,
                                      right: 2,
                                      bgcolor: alpha(statusColors.main, 0.15),
                                      color: statusColors.main,
                                      borderRadius: "4px",
                                      px: 0.4,
                                      py: 0.1,
                                      fontSize: "0.6rem",
                                      lineHeight: 1.2,
                                      fontWeight: 600,
                                      zIndex: 1,
                                      pointerEvents: "none",
                                    }}
                                  >
                                    → next day
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                    alignItems: "flex-start",
                                    width: "100%",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      width: "100%",
                                    }}
                                  >
                                    {typeInfo.icon && (
                                      <Tooltip
                                        title={typeInfo.typeName}
                                        arrow
                                        placement="top"
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            display: "inline-flex",
                                            color: statusColors.main,
                                            flexShrink: 0,
                                            lineHeight: 0,
                                          }}
                                        >
                                          {typeInfo.icon}
                                        </Box>
                                      </Tooltip>
                                    )}
                                    <Typography
                                      variant="caption"
                                      fontWeight={600}
                                      align="left"
                                      noWrap
                                      sx={{ flex: 1, minWidth: 0 }}
                                    >
                                      {svc?.ServiceTargetLanguages?.[0]
                                        ?.Language?.Name || "Language"}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    align="left"
                                    noWrap
                                  >
                                    {svc?.Client?.Account || "Client"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    align="left"
                                    noWrap
                                  >
                                    {svc?.Requestor?.Name || "Requestor"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    align="left"
                                    noWrap
                                  >
                                    {svc?.Name || "Assignment"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    align="left"
                                    noWrap
                                  >
                                    {svc?.Linguist?.Name || "Linguist"}
                                  </Typography>
                                </Box>

                                {isServiceExpanded && (
                                  <Box
                                    sx={{
                                      mt: 0.6,
                                      pt: 0.6,
                                      borderTop: `1px solid ${alpha(
                                        statusColors.main,
                                        0.3,
                                      )}`,
                                      width: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.3,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Time:{" "}
                                      <Box
                                        component="span"
                                        sx={{ fontWeight: 400 }}
                                      >
                                        {startTime} - {realEndTime} EST
                                      </Box>
                                    </Typography>
                                    {multiTzLines.slice(1).map((line) => {
                                      const separatorIndex =
                                        line.indexOf(": ");
                                      const label =
                                        separatorIndex > -1
                                          ? line.slice(0, separatorIndex)
                                          : line;
                                      const value =
                                        separatorIndex > -1
                                          ? line.slice(separatorIndex + 2)
                                          : "";
                                      return (
                                        <Typography
                                          key={line}
                                          variant="caption"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          {label}:{" "}
                                          <Box
                                            component="span"
                                            sx={{ fontWeight: 400 }}
                                          >
                                            {value}
                                          </Box>
                                        </Typography>
                                      );
                                    })}
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Status:{" "}
                                      <Box
                                        component="span"
                                        sx={{ fontWeight: 400 }}
                                      >
                                        {svc?.InterpretationStatus || "N/A"}
                                      </Box>
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Address/Link:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ wordBreak: "break-word" }}
                                    >
                                      {svc?.Address || "N/A"}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenServiceProfile(svc);
                                      }}
                                      sx={{
                                        mt: 0.6,
                                        width: "100%",
                                        pointerEvents: "auto",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      View Profile
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            </HtmlTooltip>
                          );
                        },
                      );
                    })()}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Assignment Creation Popover */}
      <Popover
        open={
          selectedStartSlot !== null && selectedEndSlot !== null && !isSelecting
        }
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setSelectedStartSlot(null);
          setSelectedEndSlot(null);
          setPopoverAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          backdrop: {
            style: { backgroundColor: "transparent" },
          },
          paper: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "8px",
              boxShadow: theme.shadows[8],
            },
          },
        }}
      >
        <Box
          sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {computeSelectedRange?.dateDisplay}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {computeSelectedRange?.startDisplay} -{" "}
              {computeSelectedRange?.endDisplay}
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleCreateAssignment}
              disabled={isLoadingOverlayData}
              sx={{ whiteSpace: "nowrap", ml: 1 }}
            >
              Create
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleContextMenuEdit}>
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuComplete}>
          <ListItemIcon>
            <CheckCircleOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Complete</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuAddTask}>
          <ListItemIcon>
            <AddTaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Task</ListItemText>
        </MenuItem>

        {/* Email threads (2026-10-06/07): only shown when the assignment has
            one or more saved links; multiple links get numbered entries.
            Every link gets an open action with a copy action right below. */}
        {parseEmailThreadIds(selectedRow?.EmailThreadID).map(
          (emailThreadId, index, threadIds) => (
            <React.Fragment key={`email-thread-${index}`}>
              <MenuItem
                onClick={() => handleContextMenuOpenEmailThread(emailThreadId)}
              >
                <ListItemIcon>
                  <OpenInNewRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {threadIds.length === 1
                    ? "Open Email Thread"
                    : `Open Email Thread ${index + 1}`}
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleContextMenuCopyEmailThreadLink(emailThreadId)
                }
              >
                <ListItemIcon>
                  <ContentCopyRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {threadIds.length === 1
                    ? "Copy Email Thread Link"
                    : `Copy Email Thread Link ${index + 1}`}
                </ListItemText>
              </MenuItem>
            </React.Fragment>
          ),
        )}

        {/* Copy the assignment's project number (2026-10-07) */}
        {selectedRow?.ProjectNumber ? (
          <MenuItem onClick={handleContextMenuCopyProjectNumber}>
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy Project Number</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>
    </Box>
  );
};

export default Calendar;
